import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { X, ArrowRight, Phone } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';
import { t } from '@/lib/translations';
import { useFocusTrap } from '@/lib/hooks/use-focus-trap';
import { GuideEmailGate } from '@/components/sections/guide-email-gate';

/**
 * ExitIntentPopup — relance éditoriale Océanne (Phase 27.47).
 *
 * Direction Sage Garden REFINED (cohérent palette site, pas dark luxury EGSF) :
 *  - Background cream ivory (var(--background))
 *  - Eyebrow terracotta uppercase tracking-[0.4em]
 *  - Title EB Garamond italic display foreground warm slate
 *  - CTA bg-primary terracotta + oceanne-cta-pulse halo
 *  - Phone fallback italic terracotta
 *
 * Triggers :
 *  - Desktop : exit intent (mouse leaves vers le top du viewport)
 *  - Mobile : 90s time-on-page sans interaction
 *
 * Skip si :
 *  - sessionStorage flag (déjà montré cette session)
 *  - localStorage flag (lead soumis < 7 jours)
 *  - prefers-reduced-motion ON (a11y)
 */

const PHONE_INTL = '14383826496';
const PHONE_DISPLAY = '438-382-6496';
const SESSION_KEY = 'oceanne-exit-popup-shown';
const LEAD_KEY = 'oceanne-lead-submitted-recently';
const MOBILE_DELAY_MS = 90 * 1000; // 90s sur mobile

export function ExitIntentPopup() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap WCAG 2.1.2 — actif uniquement quand modal ouvert
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Skip si reduced-motion preferred
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Skip si déjà montré cette session
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {
      /* sessionStorage bloqué (Safari privé) — on continue */
    }

    // Skip si lead soumis récemment (TTL 7 jours)
    try {
      const submittedAt = localStorage.getItem(LEAD_KEY);
      if (submittedAt) {
        const elapsed = Date.now() - parseInt(submittedAt, 10);
        if (elapsed < 7 * 24 * 60 * 60 * 1000) return;
      }
    } catch {
      /* localStorage bloqué — on continue */
    }

    let triggered = false;

    function show() {
      if (triggered) return;
      triggered = true;
      setOpen(true);
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        /* noop */
      }
    }

    // Desktop : mouseleave vers top viewport. Mobile : 90s time-on-page.
    const isMobile = window.matchMedia?.('(pointer: coarse)').matches;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let onMouseLeave: ((e: MouseEvent) => void) | null = null;

    if (isMobile) {
      timer = setTimeout(show, MOBILE_DELAY_MS);
    } else {
      onMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 0) show();
      };
      document.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (onMouseLeave) document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  // Cycle mount/unmount pour transition out.
  // Phase 27.72D — local disable justifié : `mounted` doit refléter `open`
  // immédiatement à l'ouverture (mount transition CSS), mais reste true 400ms
  // après fermeture pour la transition out. Ce pattern de delayed-unmount
  // n'est pas dérivable purement de props (timer asynchrone requis).
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- voir commentaire ci-dessus
      setMounted(true);
      return;
    }
    const timeout = setTimeout(() => setMounted(false), 400);
    return () => clearTimeout(timeout);
  }, [open]);

  // Lock body scroll + ESC handler
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (typeof document === 'undefined' || !mounted) return null;

  const eyebrow = t.exit_popup.eyebrow[lang];
  const titleParts = t.exit_popup.title[lang].split('\n');
  const description = t.exit_popup.description[lang];
  const ctaButton = t.exit_popup.cta_button[lang];
  const ctaGuide = t.exit_popup.cta_guide[lang];
  const orCallLabel = t.exit_popup.or_call_label[lang];
  const closeAria = t.exit_popup.close_aria[lang];

  return createPortal(
    <>
      {/* Backdrop — foreground warm slate semi-transparent (cohérent palette) */}
      <div
        aria-hidden="true"
        onClick={() => setOpen(false)}
        // data-lenis-prevent : empêche Lenis de défiler la page derrière le popup.
        data-lenis-prevent
        className="fixed inset-0 z-[55] backdrop-blur-sm transition-opacity duration-300 ease-out"
        style={{
          backgroundColor: 'rgba(58, 58, 66, 0.85)',
          opacity: open ? 1 : 0
        }}
      />

      {/* Modal */}
      <div data-lenis-prevent className="fixed inset-0 z-[56] flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="exit-popup-title"
          tabIndex={-1}
          className="pointer-events-auto relative w-full max-w-md rounded-lg overflow-hidden transition-all border bg-background"
          style={{
            borderColor: 'color-mix(in oklch, var(--secondary) 35%, transparent)',
            transitionDuration: '400ms',
            transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
            opacity: open ? 1 : 0,
            transform: open ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
            boxShadow: '0 30px 80px rgba(58, 58, 66, 0.25)'
          }}
        >
          {/* Close button — top right discret terracotta */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={closeAria}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-md transition-colors text-primary/60 hover:text-primary"
          >
            <X className="w-4 h-4" strokeWidth={1.7} />
          </button>

          <div className="p-[clamp(1.75rem,3vw,2.5rem)]">
            {/* Eyebrow terracotta uppercase tracking-[0.4em] */}
            <p className="text-[10px] uppercase tracking-[0.4em] font-bold mb-5 text-primary">
              {eyebrow}
            </p>

            {/* Title EB Garamond italic display */}
            <h2
              id="exit-popup-title"
              className="font-display italic text-[clamp(1.875rem,3vw,2.25rem)] text-foreground leading-[1.1] mb-5 text-balance"
            >
              {titleParts.map((line, i) => (
                <span key={i} className={i > 0 ? 'block' : ''}>
                  {i > 0 && <br />}
                  {line}
                </span>
              ))}
            </h2>

            {/* Description text-muted-foreground */}
            <p className="text-muted-foreground text-[clamp(0.875rem,1.2vw,1rem)] font-light leading-relaxed mb-8 text-pretty">
              {description}
            </p>

            {/* CTA principal — terracotta solid + halo oceanne-cta-pulse */}
            <Link
              to="/"
              hash="contact"
              onClick={() => setOpen(false)}
              className="oceanne-cta-pulse w-full inline-flex items-center justify-center gap-3 px-6 py-4 text-xs tracking-[0.2em] uppercase font-extrabold rounded-md transition-transform hover:-translate-y-0.5 active:scale-95 mb-3 bg-primary text-primary-foreground"
            >
              {ctaButton}
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>

            {/* CTA secondaire — gate courriel + téléchargement du guide (lead magnet de sortie) */}
            <div className="mb-5">
              <p className="mb-2.5 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-primary/70">
                {ctaGuide}
              </p>
              <GuideEmailGate />
            </div>

            {/* Phone fallback — italic terracotta discret */}
            <p className="text-center text-xs italic font-light leading-relaxed text-pretty text-primary/70">
              {orCallLabel}{' '}
              <a
                href={`tel:+${PHONE_INTL}`}
                className="font-display italic transition-opacity hover:opacity-80 inline-flex items-center gap-1.5 text-primary"
              >
                <Phone className="w-3 h-3" strokeWidth={1.7} />+1 {PHONE_DISPLAY}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
